import { ActionContext, Module } from "vuex";
import { TrucksService } from "@/TruckSelection/services/Trucks.Service";
import { Storage as storage } from "@/App/services/LocalStorage";
import type { Truck } from "@/TruckSelection/models/Truck";
import { BaseStore } from "@/Utils/vuex/BaseStore";
import { RequestState } from "@/Utils/vuex/RequestState";

const StorageKeys = {
  selectedTruck: "selectedTruck",
};

type TruckStoreState = {
  getTrucksRequestState: RequestState;
  trucks?: Truck[] | undefined;
  selectedTruck: Truck | undefined;
};

const getDefaultState = (): TruckStoreState => ({
  trucks: undefined,
  getTrucksRequestState: RequestState.Initial,
  selectedTruck: JSON.parse(storage.getItem(StorageKeys.selectedTruck) || "null"),
});

const getters = {
  trucks: (state: TruckStoreState): Truck[] | undefined => state.trucks,
  requestState: (state: TruckStoreState): RequestState => state.getTrucksRequestState,
  selectedTruck: (state: TruckStoreState): Truck | undefined => state.selectedTruck,
};

export interface TruckActions {
  selectTruck(context: ActionContext<TruckStoreState, unknown>, truck: Truck);
  loadTrucks(context: ActionContext<TruckStoreState, unknown>, emsAgencyId: String);
  resetState(context: ActionContext<TruckStoreState, unknown>);
  [key: string]: (context: ActionContext<TruckStoreState, unknown>, payload: any) => void;
}

export const actions = {
  selectTruck(context: ActionContext<TruckStoreState, unknown>, truck: Truck) {
    context.commit("selectTruck", truck);
  },
  loadTrucks(context: ActionContext<TruckStoreState, unknown>, emsAgencyId: String) {
    context.commit("setRequestState", RequestState.Loading);
    TrucksService.getData(emsAgencyId)
      .then((trucks) => {
        const mappedTrucks = trucks.map((truck) => ({ ...truck, type: truck.isALS ? "ALS" : "BLS" }));
        context.commit("setTrucks", mappedTrucks);
        context.commit("setRequestState", RequestState.LoadingSucceeded);
      })
      .catch(() => {
        context.commit("setRequestState", RequestState.LoadingFailed);
      });
  },
  resetState(context: ActionContext<TruckStoreState, unknown>) {
    context.commit("resetState");
  },
} as TruckActions;

const mutations = {
  setRequestState(state: TruckStoreState, newRequestState: RequestState) {
    state.getTrucksRequestState = newRequestState;
  },
  setTrucks(state: TruckStoreState, trucks: Truck[]): void {
    state.trucks = trucks;
  },
  selectTruck(state: TruckStoreState, truck: Truck): void {
    state.selectedTruck = truck;
    storage.setItem(StorageKeys.selectedTruck, JSON.stringify(truck));
  },
  resetState(state: TruckStoreState): void {
    const initialState: TruckStoreState = getDefaultState();
    Object.keys(initialState).forEach((key) => {
      state[key] = initialState[key];
    });
  },
};

interface TrucksStore extends BaseStore<TruckStoreState, TruckActions, any, any> {
  state: TruckStoreState;
  actions: TruckActions;
}

const store: TrucksStore = {
  namespaced: true,
  state: getDefaultState(),
  getters,
  actions,
  mutations,
} satisfies Module<TruckStoreState, unknown>;

export default store;
